import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { startKeycloakLogin } from '@/services/oidcFlow'

// Lazy-loaded components for code splitting
const LandingPage = () => import('@/views/public/LandingPage.vue')
const LoginView = () => import('@/views/LoginView.vue')
const RegisterView = () => import('@/views/RegisterView.vue')
const EmailVerificationView = () => import('@/views/EmailVerificationView.vue')
const EmailVerificationFormView = () => import('@/views/EmailVerificationFormView.vue')
const UsersView = () => import('@/views/admin/UsersView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
const SetupView = () => import('@/views/SetupView.vue')
const AdminConfigView = () => import('@/views/admin/ConfigView.vue')
const AdminAuthToolsView = () => import('@/views/admin/AuthToolsView.vue')
const AdminDatabaseView = () => import('@/views/admin/DatabaseView.vue')
const AdminUsageView = () => import('@/views/admin/UsageView.vue')
const AdminProvidersView = () => import('@/views/admin/ProvidersView.vue')
const AdminProviderDetailView = () => import('@/views/admin/ProviderDetailView.vue')
const GamesView = () => import('@/views/admin/GamesView.vue')
const GameShowsView = () => import('@/views/admin/GameShowsView.vue')
const ModeratorGameShowView = () => import('@/views/moderator/GameShowView.vue')
const GameShowPreviewView = () => import('@/views/GameShowPreviewView.vue')
const TeamGameShowView = () => import('@/views/teams/GameShowView.vue')
const GameShowJoinQrView = () => import('@/views/GameShowJoinQrView.vue')
const KeycloakCallbackView = () => import('@/views/KeycloakCallbackView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const ImagesView = () => import('@/views/ImagesView.vue')
const VideosView = () => import('@/views/VideosView.vue')
const AssetsView = () => import('@/views/AssetsView.vue')
const DashboardView = () => import('@/views/DashboardView.vue')
const DataTransferView = () => import('@/views/DataTransferView.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: LandingPage,
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardView,
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView,
    },
    {
      path: '/verify-email/:token',
      name: 'email-verification',
      component: EmailVerificationView,
    },
    {
      path: '/verify-email-form',
      name: 'email-verification-form',
      component: EmailVerificationFormView,
    },
    {
      path: '/auth/callback',
      name: 'keycloak-callback',
      component: KeycloakCallbackView,
    },
    {
      path: '/verify-email-change/:token',
      name: 'email-change-verification',
      component: EmailVerificationView,
    },
    {
      path: '/users',
      name: 'users',
      component: UsersView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/config',
      name: 'admin-config',
      component: AdminConfigView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/auth-tools',
      name: 'admin-auth-tools',
      component: AdminAuthToolsView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/database',
      name: 'admin-database',
      component: AdminDatabaseView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/usage',
      name: 'admin-usage',
      component: AdminUsageView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/providers',
      name: 'admin-providers',
      component: AdminProvidersView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/providers/:providerId',
      name: 'admin-providers-detail',
      component: AdminProviderDetailView,
      meta: { requiresAuth: true },
    },
    {
      path: '/games',
      name: 'games',
      component: GamesView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/images',
      name: 'images',
      component: ImagesView,
      meta: { requiresAuth: true },
    },
    {
      path: '/videos',
      name: 'videos',
      component: VideosView,
      meta: { requiresAuth: true },
    },
    {
      path: '/assets',
      name: 'assets',
      component: AssetsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/gameshows',
      name: 'gameshows',
      component: GameShowsView,
      meta: { requiresAuth: true },
    },
    {
      path: '/gameshows/:id',
      name: 'gameshow-detail',
      component: ModeratorGameShowView,
      meta: { requiresAuth: true },
    },
    {
      path: '/gameshows/:id/preview',
      name: 'gameshow-preview',
      component: GameShowPreviewView,
      meta: { requiresAuth: true },
    },
    // New public play routes
    {
      path: '/play/:publicId',
      name: 'play-gameplay',
      component: TeamGameShowView,
    },
    {
      path: '/play/:publicId/login',
      name: 'play-login',
      component: TeamGameShowView,
    },
    {
      path: '/play/:publicId/register',
      name: 'play-register',
      component: TeamGameShowView,
    },
    {
      path: '/play/:publicId/qr',
      name: 'play-qr',
      component: GameShowJoinQrView,
    },
    // Backwards compatibility redirects from old /join paths
    {
      path: '/join/:publicId',
      redirect: (to) => ({ name: 'play-login', params: { publicId: to.params.publicId } }),
    },
    {
      path: '/join/:publicId/qr',
      redirect: (to) => ({ name: 'play-qr', params: { publicId: to.params.publicId } }),
    },
    {
      path: '/data-transfer',
      name: 'data-transfer',
      component: DataTransferView,
      meta: { requiresAuth: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView,
      meta: { requiresAuth: true },
    },
    {
      path: '/setup',
      name: 'setup',
      component: SetupView,
    },
    {
      path: '/404',
      name: 'not-found',
      component: NotFoundView,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'catch-all',
      component: NotFoundView,
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Helper: team session presence for play routes
  const isPlayRoute = (name?: string | symbol) => {
    const n = String(name || '')
    return n.startsWith('play-') || n === 'play-gameplay'
  }
  const getTeamSessionExists = (toRoute: any) => {
    if (!isPlayRoute(toRoute.name)) return false
    const publicId = toRoute.params?.publicId as string
    if (!publicId) return false
    const sessionKey = `team_session_${publicId}`
    return !!localStorage.getItem(sessionKey)
  }
  
  // Check if auth is configured
  await authStore.checkAuthConfig()
  
  // Block access to setup route if setup is already complete
  if (to.name === 'setup' && !authStore.setupRequired) {
    // Setup is complete, show 404 page
    next({ name: 'not-found' })
    return
  }
  
  // Redirect to setup if required
  if (authStore.setupRequired && to.name !== 'setup') {
    next({ name: 'setup' })
    return
  }

  // Public play route guards based on team session
  if (String(to.name) === 'play-gameplay' && !getTeamSessionExists(to)) {
    next({ name: 'play-login', params: { publicId: to.params.publicId } })
    return
  }
  if ((String(to.name) === 'play-login' || String(to.name) === 'play-register') && getTeamSessionExists(to)) {
    next({ name: 'play-gameplay', params: { publicId: to.params.publicId } })
    return
  }

  // Optionally start application-owned OIDC before rendering the landing page.
  if (
    to.name === 'home' &&
    authStore.authEnabled &&
    !authStore.isAuthenticated &&
    authStore.keycloakAuthEnabled &&
    authStore.oidcAutoRedirect
  ) {
    try {
      await startKeycloakLogin()
      return
    } catch (error: any) {
      next({
        name: 'login',
        query: {
          oidc_error: error?.response?.data?.detail || error?.message || 'Keycloak login could not be started.',
        },
      })
      return
    }
  }

  // If landing page is disabled and user is not authenticated, redirect to login
  if (to.name === 'home' && authStore.authEnabled && !authStore.isAuthenticated && !authStore.landingPageEnabled) {
    next({ name: 'login' })
    return
  }

  // If user is authenticated, visiting home should go to dashboard
  if (to.name === 'home' && authStore.authEnabled && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
    return
  }

  // If already authenticated, prevent access to login/register and redirect home
  if (authStore.authEnabled && authStore.isAuthenticated && (to.name === 'login' || to.name === 'register')) {
    next({ name: 'dashboard' })
    return
  }

  // Prevent access to register when registration is disabled or auth is off
  if (to.name === 'register') {
    if (!authStore.authEnabled || !authStore.userRegistrationEnabled) {
      next({ name: 'home' })
      return
    }
  }
  
  // Check authentication for protected routes
  if (to.meta.requiresAuth && authStore.authEnabled && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }
  
  // Check admin role for admin-only routes
  if (to.meta.requiresAdmin && authStore.authEnabled && authStore.isAuthenticated && !authStore.isAdmin) {
    next({ name: 'home' })
    return
  }
  
  next()
})

export default router
